import type { ImagesOverviewProps } from './data';

import { useState } from 'react';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';

import LikeButton from '../LikeButton';

import {
  Box,
  Icon,
  IconButton,
  Image,
  SimpleGrid,
  Stack,
  Tag,
  TagLabel,
} from '@/components';
import { useDisclosure, useProfile, useShare } from '@/hooks';
import Dots from '@/svgs/products/dots.svg';
import Share from '@/svgs/products/share.svg';
import { googleEventShare } from '@/utils/googleAnalyticsEvent';

const GalleryModal = dynamic(() => import('../GalleryModal'), {
  ssr: false,
});

const ImagesOverview: ImagesOverviewProps = ({
  galleryImages,
  homeImage,
  discounts,
  userImages,
  code,
  userFavoriteProductId,
  isOutOfStock,
  title,
  productId,
  price,
  discountPrice,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [initialSlide, setInitialSlide] = useState(0);
  const [tabIndex, setTabIndex] = useState(0);
  const { data: profile } = useProfile();
  const handleShare = useShare();
  const pathname = usePathname();

  const handleMainImage = () => {
    setInitialSlide(0);
    onOpen();
  };
  return (
    <Stack
      justify="space-between"
      maxH="680px"
      align="center"
      direction="column"
      gap={{ base: '6', md: '6' }}
      flexWrap="wrap"
    >
      <Box
        cursor="pointer"
        sx={{
          position: 'relative',
          borderRadius: { base: 'md', md: '22px' },
          h: { base: '307px', md: '460px' },
          w: { base: '100%', md: '620px' },
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: '#fff',
          '.img': {
            h: { base: '307px', md: '460px' },
            w: { base: '307px', md: '460px' },
          },
        }}
      >
        <Stack
          direction="column"
          gap="8px"
          display={{ base: 'flex', xl: 'none' }}
          position="absolute"
          left={{ base: '16px', xl: 0 }}
          top="18px"
        >
          {profile && (
            <LikeButton
              code={code}
              itemId={productId}
              title={title}
              discountPrice={discountPrice}
              price={price}
              userFavoriteProductId={userFavoriteProductId}
            />
          )}
          <IconButton
            variant="none"
            border="none"
            aria-label="share"
            icon={
              <Icon
                as={Share}
                width={{ base: '17px', md: '26px' }}
                height={{ base: '19px', md: '23px' }}
                strokeWidth={2}
                onClick={() => {
                  googleEventShare(productId);
                  void handleShare({
                    title: title,
                    url: `${process.env.NEXT_PUBLIC_CLIENT_URL}${pathname}`,
                  });
                }}
              />
            }
          />
        </Stack>
        {!isOutOfStock && (
          <Stack
            flexDirection={{ base: 'row', md: 'column' }}
            position="absolute"
            right={{ base: '56px', md: '0' }}
            zIndex="docked"
            direction="column"
            gap={{ base: '5px', md: 4 }}
            top={{ base: '0', md: '75px' }}
          >
            {discounts?.map((discount) => (
              <Tag
                fontSize={{ base: 'sm', md: 'lg' }}
                key={discount.title}
                size={{ base: 'sm', md: 'lg' }}
                variant={{ base: 'vertical', md: 'promo' }}
                bg={
                  discount.type === 'extra_retail_percent'
                    ? 'secondary.600'
                    : 'primary.500'
                }
              >
                <TagLabel>{discount.percent}%</TagLabel>
              </Tag>
            ))}
          </Stack>
        )}
        <Image
          src={homeImage?.home_page_image}
          alt={homeImage?.alt_home_page_image}
          className="img"
          height={640}
          width={640}
          onClick={handleMainImage}
        />
      </Box>
      <SimpleGrid
        columns={{ base: 3, md: 3 }}
        w={{ base: '100%', md: '620px' }}
        gap={{ base: '3', md: '6' }}
      >
        {galleryImages.slice(0, 3).map((img, index) => (
          <Stack
            cursor="pointer"
            key={img}
            display="flex"
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: { base: 'sm', md: '10px' },
              border: '1px solid',
              borderColor: 'primary.50',
              position: 'relative',
              '.img': {
                filter: index === 2 ? 'blur(2px)' : '',
                bg: 'transparent',
                opacity: index === 2 ? '20%' : 1,
                w: { base: '105px', md: '170px' },
                h: { base: '105px', md: '170px' },
                objectFit: 'cover',
              },
            }}
          >
            <Icon
              display={index === 2 ? 'inline-block' : 'none'}
              as={Dots}
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%);"
              width={{ base: '28px', md: '36px' }}
              height={{ base: '15px', md: '8px' }}
            />
            <Box
              onClick={() => {
                setTabIndex(0);
                setInitialSlide(index);
              }}
            >
              <Image
                src={img}
                alt={img}
                className="img"
                height={200}
                width={200}
                onClick={onOpen}
              />
            </Box>
          </Stack>
        ))}
      </SimpleGrid>
      {isOpen && (
        <GalleryModal
          tabIndex={tabIndex}
          initialSlide={initialSlide}
          isOpen={isOpen}
          onClose={onClose}
          userImages={userImages}
          galleryImages={galleryImages}
          setTabIndex={setTabIndex}
        />
      )}
    </Stack>
  );
};

export default ImagesOverview;
